"use client";

import clsx from "clsx";
import { useEffect, useRef, type CSSProperties, type ReactNode } from "react";
import Swiper from "swiper";
import { Navigation, Pagination } from "swiper/modules";
import "./Carousel.css";

// Points left; the next button turns it round. Painted through a mask so it takes the button's colour.
const CHEVRON =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M15 18l-6-6 6-6'/%3E%3C/svg%3E\")";

const BUTTON =
  "relative block size-9 p-0 border-0 rounded-full cursor-pointer appearance-none transition-[background-color,opacity] duration-300 ease-[ease] disabled:cursor-default " +
  "before:content-[''] before:absolute before:top-1/2 before:left-1/2 before:size-4 before:-mt-2 before:-ml-2 before:bg-current before:mask-(--chevron) before:mask-no-repeat before:mask-center before:mask-contain";

/**
 * Swiper disables the buttons at either end itself (disabledClass, plus the
 * disabled attribute it sets on a <button>), so the disabled look rides on :disabled.
 */
const TONES = {
  dark: "bg-das-black text-white disabled:bg-[#d1d1d6]",
  light:
    "bg-white/90 text-das-black disabled:bg-white/20 disabled:text-white/50",
};

type Props = {
  /** dark (the default) for light sections, light for dark ones. */
  tone?: "dark" | "light";
  /**
   * Lets the neighbouring slides spill past the viewport instead of being
   * clipped, so the width of the shell around it reads as the active slide's
   * width. The section itself has to clip the overspill.
   */
  bleed?: boolean;
  /** Percent the run sits right of centre until the reader first moves it. */
  shift?: number;
  /**
   * The .swiper-slide elements. Each sizes itself (w-auto! h-auto! unless the
   * section wants a width), the ! because Swiper's own .swiper-slide sets both.
   */
  children: ReactNode;
};

/**
 * The port of das-carousel.js: the centred, free-width carousel shared by the
 * DAS Academies v2 sections.
 *
 * Slides size themselves so a section can mix widths (the experience cards
 * have one wider "featured" card), which is why slidesPerView is auto rather
 * than a number.
 *
 * Swiper runs on the markup as rendered, so the slides stay server-rendered.
 * The bullets are the one thing it creates itself; they are styled in
 * Carousel.css, under the das-carousel class on the root.
 */
export default function Carousel({
  tone = "dark",
  bleed = false,
  shift,
  children,
}: Props) {
  const carouselRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const controlsRef = useRef<HTMLDivElement>(null);
  const paginationRef = useRef<HTMLDivElement>(null);
  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const carousel = carouselRef.current;
    const viewport = viewportRef.current;
    const controls = controlsRef.current;

    if (!carousel || !viewport || !controls) {
      return;
    }

    const swiper = new Swiper(viewport, {
      modules: [Navigation, Pagination],
      slidesPerView: "auto",
      centeredSlides: true,
      spaceBetween: 16,
      speed: 500,
      grabCursor: true,
      watchOverflow: true,
      breakpoints: {
        768: {
          spaceBetween: 20,
        },
      },
      pagination: {
        el: paginationRef.current,
        clickable: true,
        bulletClass: "das-carousel__bullet",
        bulletActiveClass: "das-carousel__bullet--active",
      },
      navigation: {
        prevEl: prevRef.current,
        nextEl: nextRef.current,
        disabledClass: "das-carousel__btn--disabled",
      },
    });

    /**
     * The section's opening offset is a first impression only: at the first
     * sign the reader is reaching for the carousel it is dropped, and the run
     * centres from then on.
     *
     * Reaching for the controls is the earliest signal there is, so the run has
     * already centred by the time the click lands rather than moving twice.
     * That only reads for a pointer, so the two Swiper events stay as the way it
     * settles for everyone else: a tap on an arrow or a bullet changes the
     * slide, and a drag too short to change it still has to let the offset go
     * rather than leave the run sitting off to one side mid-gesture.
     *
     * The property is removed rather than set to zero because it arrives inline
     * from the section, and nothing in the stylesheet can outrank that.
     */
    const settle = () => carousel.style.removeProperty("--das-carousel-shift");

    controls.addEventListener("mouseenter", settle, { once: true });
    swiper.once("slideChange", settle);
    swiper.once("touchStart", settle);

    return () => {
      controls.removeEventListener("mouseenter", settle);
      swiper.destroy(true, true);
    };
  }, []);

  const style = shift
    ? ({ "--das-carousel-shift": `${shift}%` } as CSSProperties)
    : undefined;
  const chevron = { "--chevron": CHEVRON } as CSSProperties;

  /**
   * The viewport carries Swiper's .swiper, whose overflow (and the slides'
   * width) would otherwise outrank a utility, hence the !.
   *
   * --das-carousel-shift slides the run sideways without Swiper knowing about
   * it, so the slide widths and snap positions it measures are untouched. It
   * arrives inline from the section and settle() removes it, which drops the
   * run back to 0; the transition matches Swiper's own 500ms, so the run
   * un-nudges in the same movement as the slide it is changing to.
   */
  return (
    <div
      ref={carouselRef}
      className="das-carousel w-full js-das-carousel"
      data-tone={tone}
      style={style}
    >
      <div
        ref={viewportRef}
        className={clsx(
          "swiper w-full [transform:translateX(var(--das-carousel-shift,0))] transition-[transform] duration-500 ease-[ease-out]",
          bleed ? "overflow-visible!" : "overflow-hidden",
        )}
      >
        <div className="swiper-wrapper">{children}</div>
      </div>

      <div
        ref={controlsRef}
        className="flex items-center justify-center mt-7 mobile:mt-5.5"
      >
        {/*
         * Swiper stamps its pagination classes on here, and with them a full
         * width from a two-class selector; back in the flow, the bullets sit
         * beside the arrows.
         */}
        <div
          ref={paginationRef}
          className="static flex items-center w-auto!"
        ></div>

        <div className="flex items-center ml-4">
          <button
            ref={prevRef}
            type="button"
            className={clsx(BUTTON, TONES[tone])}
            style={chevron}
            aria-label="Previous slide"
          ></button>
          <button
            ref={nextRef}
            type="button"
            className={clsx(BUTTON, TONES[tone], "ml-2 before:rotate-180")}
            style={chevron}
            aria-label="Next slide"
          ></button>
        </div>
      </div>
    </div>
  );
}
